import endpoints from "../api/endpoints";
import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { BUCKET_DEVICES } from "../Constants";
import DataGridTable from "../Components/DataGridTable";
import axios from "axios";
import dayjs from "dayjs";
import moment from 'moment';
import Spinner from '../Components/Spinner';
import { useAuth } from "../hooks/AuthProvider";

import { LanguageContext } from "../context/LanguageContext";
import notifTranslations from "../languages/notification.json";
import Alert from '../Components/Alert';
import './Notifications.css';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { GridActionsCellItem } from '@mui/x-data-grid';

export const Notifications = () => {

	const { org, permissions } = useAuth();

	const canViewNotifications = permissions['view_notification'];
	const canDeleteNotifications = permissions['delete_device_data'];

	const { lang } = useContext(LanguageContext);
	const tPage = notifTranslations[lang].notifications;
	const isRtl = ['ar', 'ur'].includes(lang);
	const [viewingNotification, setViewingNotification] = useState({
		open: false,
		data: null
	});
	const initialRange = {
		tstart: dayjs().subtract(1, 'day').unix(),
		tstop: dayjs().add(2, 'hour').unix(),
		inputValue: 1,
		timeUnit: 'day'
	};
	const [data, setData] = useState({ rows: [] });
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [rowCount, setRowCount] = useState(0);
	const [rangeVersion, setRangeVersion] = useState(0);
	const timeRange = useRef(initialRange);
	const [dateRange, setDateRange] = useState({
		from: dayjs().subtract(1, 'day').format("YYYY-MM-DDTHH:mm"),
		to: dayjs().format("YYYY-MM-DDTHH:mm")
	});
	const [alertState, setAlertState] = useState({
		open: false,
		message: '',
		type: 'info',
		position: isRtl ? 'left' : 'right'
	});

	const showAlert = useCallback((type, message) => {
		setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
	}, [isRtl]);

	const mapNotificationRows = useCallback((items) => {
		return (Array.isArray(items) ? items : []).slice(0, pageSize).map((r, idx) => ({
			rowId: `${r._time || "t"}-${r.id || "n"}-${idx}`,
			_time: new Date(r._time),
			sourceDid: r?.source_did ?? r?.sourceDevice ?? r?.did ?? "",
			device: r?.source_did ?? r?.sourceDevice ?? r?.did ?? "",
			deviceName:
				r?.source_device_name ??
				r?.source_device?.name ??
				r?.automation?.device?.name ??
				"",
			template:
				r?.source_device?.template?.name ??
				r?.automation?.template?.name ??
				r?.automation?.tid ??
				"",
			organization:
				r?.source_device?.organization?.name ??
				r?.source_device?.organization_name ??
				r?.automation?.organization?.name ??
				r?.automation?.organization_name ??
				r?.automation?.device?.organization?.name ??
				r?.automation?.device?.organization_name ??
				"",
			message: r.message ?? r.description ?? "",
			notificationType: r.notificationType ?? "",
			source: r.trigger ?? r.source ?? "",
			raw: r
		}));
	}, [pageSize]);

	const applyNotificationPayload = useCallback((items) => {
		setData({ rows: mapNotificationRows(items) });
	}, [mapNotificationRows]);

	const resolvePaginationTotal = useCallback((items, pagination) => {
		const rowsLength = Array.isArray(items) ? items.length : 0;
		const parsedTotal = Number(pagination?.total);

		if (Number.isFinite(parsedTotal) && parsedTotal >= 0) {
			return parsedTotal;
		}

		return rowsLength;
	}, []);

	const streamRef = useRef(null);
	const retryTimeoutRef = useRef(null);
	const hasReceivedPayloadRef = useRef(false);
	const streamRequestKeyRef = useRef("");
	const knownTotalRef = useRef({ key: "", total: 0 });

	const buildNotificationQueryParams = useCallback(() => ({
		bucket: BUCKET_DEVICES,
		tstart: timeRange.current.tstart,
		tstop: timeRange.current.tstop,
		page,
		pageSize
	}), [page, pageSize]);

	const buildNotificationDatasetKey = useCallback(() => {
		return [
			org?.id || "",
			BUCKET_DEVICES,
			timeRange.current.tstart,
			timeRange.current.tstop,
		].join("|");
	}, [org?.id]);

	const fetchData = useCallback(() => {
		if (!canViewNotifications) {
			hasReceivedPayloadRef.current = false;
			streamRequestKeyRef.current = "";
			knownTotalRef.current = { key: "", total: 0 };
			setData({ rows: [] });
			setRowCount(0);
			setLoading(false);
			return;
		}

		if (!org?.id) {
			hasReceivedPayloadRef.current = false;
			streamRequestKeyRef.current = "";
			knownTotalRef.current = { key: "", total: 0 };
			setData({ rows: [] });
			setRowCount(0);
			setLoading(false);
			return;
		}

		const queryParams = buildNotificationQueryParams();
		const datasetKey = buildNotificationDatasetKey();
		const streamRequestKey = `${datasetKey}|${queryParams.page}|${queryParams.pageSize}`;

		streamRequestKeyRef.current = streamRequestKey;
		hasReceivedPayloadRef.current = false;
		setLoading(true);

		if (retryTimeoutRef.current) {
			clearTimeout(retryTimeoutRef.current);
			retryTimeoutRef.current = null;
		}

		const connectStream = () => {
			try {
				if (streamRef.current) {
					streamRef.current.close();
					streamRef.current = null;
				}

				const evtSource = new EventSource(
					endpoints.data.notificationsSse(org.id, queryParams),
					{ withCredentials: true }
				);

				streamRef.current = evtSource;

				evtSource.onmessage = (event) => {
					if (streamRequestKeyRef.current !== streamRequestKey) return;

					try {
						const res = JSON.parse(event.data);

						if (res?.status === "ERROR") {
							console.warn("[Notifications] Ignoring SSE error payload:", res);
							showAlert('error', res.message || 'Failed to load notifications stream.');
							setLoading(false);
							return;
						}

						if (Array.isArray(res?.data)) {
							hasReceivedPayloadRef.current = true;
							applyNotificationPayload(res.data);
							const nextTotal = resolvePaginationTotal(res.data, res?.pagination);
							knownTotalRef.current = { key: datasetKey, total: nextTotal };
							setRowCount(nextTotal);
							setLoading(false);
							return;
						}

						console.warn("[Notifications] Ignoring unexpected SSE payload:", res);
						setLoading(false);
					} catch (err) {
						console.error("Error parsing notifications stream data:", err);
						setLoading(false);
					}
				};

				evtSource.onerror = (err) => {
					if (streamRequestKeyRef.current !== streamRequestKey) return;

					console.error("Notifications stream connection error:", err);
					evtSource.close();
					streamRef.current = null;

					if (!hasReceivedPayloadRef.current) setLoading(false);

					if (!retryTimeoutRef.current) {
						retryTimeoutRef.current = setTimeout(() => {
							retryTimeoutRef.current = null;
							connectStream();
						}, 3000);
					}
				};
			} catch (err) {
				console.error("Failed to initialize notifications stream:", err);
				setLoading(false);
			}
		};

		connectStream();
	}, [
		applyNotificationPayload,
		buildNotificationDatasetKey,
		buildNotificationQueryParams,
		canViewNotifications,
		org?.id,
		resolvePaginationTotal,
		showAlert
	]);

	const applyDateRange = useCallback(() => {
		const tstart = dayjs(dateRange.from).unix();
		const tstop = dayjs(dateRange.to).unix();

		timeRange.current = { tstart, tstop };
		setPage(1);
		setRangeVersion((value) => value + 1);
	}, [dateRange]);

	const refreshNotifications = useCallback(() => {
		knownTotalRef.current = { key: "", total: 0 };
		setRangeVersion((value) => value + 1);
	}, []);

	const handleDeleteNotification = useCallback(async (row) => {
		if (!canDeleteNotifications || !org?.id) return;

		const raw = row?.raw || {};
		const measurement = raw._measurement;
		const time = raw._time || row?._time;

		if (!measurement || !time) {
			showAlert('error', 'Cannot delete this notification because its identity is incomplete.');
			return;
		}

		const confirmed = window.confirm('Delete this automation notification from Influx? This cannot be undone.');
		if (!confirmed) return;

		try {
			await axios.delete(endpoints.data.deleteNotification(org.id), {
				data: {
					bucket: BUCKET_DEVICES,
					time,
					measurement,
					automation_id: raw.id,
					source_did: raw.source_did || raw.sourceDevice || raw.did || row?.sourceDid || undefined
				}
			});
			showAlert('success', 'Notification deleted.');
			refreshNotifications();
		} catch (err) {
			showAlert('error', err?.response?.data?.message || err?.message || 'Failed to delete notification.');
		}
	}, [canDeleteNotifications, org?.id, refreshNotifications, showAlert]);

	const handleClearNotifications = useCallback(async () => {
		if (!canDeleteNotifications || !org?.id) return;

		const confirmed = window.confirm(
			'Clear all automation notifications for this organization scope from Influx? This cannot be undone.'
		);
		if (!confirmed) return;

		try {
			await axios.delete(endpoints.data.clearNotifications(org.id), {
				data: {
					bucket: BUCKET_DEVICES,
					confirm: 'CLEAR_AUTOMATION_NOTIFICATIONS'
				}
			});
			showAlert('success', 'Automation notifications cleared.');
			setPage(1);
			refreshNotifications();
		} catch (err) {
			showAlert('error', err?.response?.data?.message || err?.message || 'Failed to clear notifications.');
		}
	}, [canDeleteNotifications, org?.id, refreshNotifications, showAlert]);

	useEffect(() => {
		setPage(1);
	}, [org?.id]);

	useEffect(() => {
		fetchData();
	}, [fetchData, rangeVersion]);

	useEffect(() => {
		return () => {
			if (streamRef.current) {
				streamRef.current.close();
				streamRef.current = null;
			}
			if (retryTimeoutRef.current) {
				clearTimeout(retryTimeoutRef.current);
				retryTimeoutRef.current = null;
			}
			hasReceivedPayloadRef.current = false;
			streamRequestKeyRef.current = "";
		};
	}, [org?.id]);

	const columns = [
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem
					icon={<VisibilityOutlinedIcon />}
					label="View"
					showInMenu
					onClick={() => {
						setViewingNotification({
							open: true,
							data: params.row
						});
					}}
				/>,
				...(canDeleteNotifications ? [
					<GridActionsCellItem
						icon={<DeleteOutlineOutlinedIcon />}
						label="Delete"
						showInMenu
						onClick={() => handleDeleteNotification(params.row)}
					/>
				] : [])
			]
		},
		{
			field: '_time',
			headerName: tPage.columns.time,
			type: 'dateTime',
			headerAlign: 'center',
			align: "center",
		},

		{
			field: 'deviceName',
			headerName: tPage.columns.deviceName || 'Device Name',
			headerAlign: 'center',
			align: "center"
		},
		{
			field: 'organization',
			headerName: tPage.columns.organization,
			headerAlign: 'center',
			align: "center"
		},
		{
			field: 'template',
			headerName: tPage.columns.template || 'Template',
			headerAlign: 'center',
			align: "center"
		},

		{
			field: 'source',
			headerName: tPage.columns.source,
			headerAlign: 'center',
			align: "center"
		},
		{
			field: 'notificationType',
			headerName: tPage.columns.notificationType,
			headerAlign: 'center',
			align: "center"
		},
	];
	const rows = data.rows;
	const pagingoptions = [10, 20, 50, 100];
	const idcolumn = 'rowId';
	const sortField = '_time';
	const sortType = 'desc';

	if (!canViewNotifications) {
		return (
			<div className="grid-container notif-permission-denied">
				<h2>Permission Denied</h2>
				<p>You do not have the required permission to view notifications.</p>
			</div>
		);
	}

	return (
		<div className="configuration">
			<Alert
				open={alertState.open}
				close={() => setAlertState({ ...alertState, open: false })}
				message={alertState.message}
				type={alertState.type}
				position={alertState.position}
			/>
			<NotificationDetailsModal
				state={viewingNotification}
				setState={setViewingNotification}
			/>
			<div>
				<div
					className="date-range-container"
					style={{
						display: 'flex',
						gap: '12px',
						alignItems: 'center',
						flexWrap: 'wrap'
					}}
				>
					<input
						type="datetime-local"
						value={dateRange.from}
						onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
					/>

					<input
						type="datetime-local"
						value={dateRange.to}
						onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
					/>

					<button
						onClick={applyDateRange}
						disabled={loading}
						className="devices-primary-btn"
					>
						Apply
					</button>
					{canDeleteNotifications && (
						<button
							onClick={handleClearNotifications}
							disabled={loading || (!rowCount && rows.length === 0)}
							className="devices-primary-btn notif-danger-btn"
						>
							Clear All
						</button>
					)}
				</div>		</div>
			{loading ?
				<Spinner />
				: rowCount > 0 || rows.length > 0 ?
					<div className="devices-grid-wrapper">
						<DataGridTable
							rows={rows}
							columns={columns}
							idcolumn={idcolumn}
							pagingoptions={pagingoptions}
							sortField={sortField}
							sortType={sortType}
							showToolbar={true}
							tableKey='notifications'
							loading={loading}
							pagination={{
								serverSide: true,
								page,
								pageSize,
								count: rowCount,
								onPageChange: (newPage) => setPage(newPage),
								onPageSizeChange: (newSize) => {
									setPageSize(newSize);
									setPage(1);
								}
							}}
							handleRowClick={(params) => {
								setViewingNotification({
									open: true,
									data: params.row
								});
							}}
						/>
					</div>
					: <div className="notif-no-data">
						{tPage.noData}
					</div>
			}
		</div>
	);
};
const NotificationDetailsModal = ({ state, setState }) => {

	const { lang } = useContext(LanguageContext);
	const isRtl = ['ar', 'ur'].includes(lang);

	if (!state?.open) return null;

	const closeModal = () => {
		setState({
			open: false,
			data: null
		});
	};

	const data = state?.data || {};

	return (
		<div id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
			<div id="popup" className="dashboard-modal">

				<div className="dashboard-header">
					<div className="dashboard-header-text">
						<h2>Notification Details</h2>
						<p>{data?.message || '-'}</p>
					</div>
				</div>

				<div className="modal-divider" />

				<div className="modal-content dashboard-content">
					<div className="form-section">

						<div className="form-item">
							<label className="form-lbl">Time</label>
							<input
								className="add-column"
								value={
									data?._time
										? moment(data._time).format('YYYY-MM-DD HH:mm:ss')
										: ''
								}
								readOnly
							/>
						</div>

						<div className="form-item">
							<label className="form-lbl">Device</label>
							<input
								className="add-column"
								value={data?.device || '-'}
								readOnly
							/>
						</div>

						<div className="form-item">
							<label className="form-lbl">Device Name</label>
							<input
								className="add-column"
								value={data?.deviceName || '-'}
								readOnly
							/>
						</div>

						<div className="form-item">
							<label className="form-lbl">Source DID</label>
							<input
								className="add-column"
								value={data?.sourceDid || '-'}
								readOnly
							/>
						</div>
						<div className="form-item">
							<label className="form-lbl">Organization</label>
							<input
								className="add-column"
								value={data?.organization || '-'}
								readOnly
							/>
						</div>
						<div className="form-item">
							<label className="form-lbl">Template</label>
							<input
								className="add-column"
								value={data?.template || '-'}
								readOnly
							/>
						</div>



						<div className="form-item">
							<label className="form-lbl">Source</label>
							<input
								className="add-column"
								value={data?.source || '-'}
								readOnly
							/>
						</div>
						<div className="form-item">
							<label className="form-lbl">Notification Type</label>
							<input
								className="add-column"
								value={data?.notificationType || '-'}
								readOnly
							/>
						</div>

						<div className="form-item">
							<label className="form-lbl">Message</label>
							<textarea
								className="add-column"
								value={data?.message || ''}
								readOnly
								rows={5}
							/>
						</div>

					</div>
				</div>

				<div className="modal-footer dashboard-footer">
					<div />
					<div className="modal-footer-right-buttons">
						<button className="btn btn-cancel" onClick={closeModal}>
							Close
						</button>
					</div>
				</div>

			</div>
		</div>
	);
};
